import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider, Switch } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined, CreditCardOutlined,
    VisibilityOutlined, VisibilityOffOutlined,
    ContentCopyOutlined, CheckOutlined,
    NorthEastOutlined, ShoppingBagOutlined, WifiOutlined,
    RefreshOutlined, BlockOutlined, CheckCircleOutline,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    blue:    '#007AFF',
};

const CARD = {
    number:  '4532 8801 4371 2209',
    expiry:  '09/28',
    cvv:     '472',
    name:    'AHMED BELLO',
    balance: 125000,
    spent:   34500,
    limit:   200000,
    network: 'Visa',
};

const RECENT_TXS = [
    { id: 1, title: 'Shoprite Purchase',   sub: 'Card Payment',   amount: 8500,  date: 'Today · 3:22 PM',      icon: ShoppingBagOutlined, color: '#FF6B35' },
    { id: 2, title: 'Netflix Subscription', sub: 'Online Payment', amount: 4600,  date: 'Yesterday · 12:00 AM', icon: WifiOutlined,         color: '#E50914' },
    { id: 3, title: 'Amazon Purchase',      sub: 'Online Payment', amount: 22000, date: 'Mar 8 · 1:35 PM',      icon: ShoppingBagOutlined, color: '#FF9500' },
    { id: 4, title: 'Uber Ride',             sub: 'Card Payment',   amount: 3400,  date: 'Mar 6 · 7:14 PM',      icon: NorthEastOutlined,   color: B.blue   },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const CardPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [showDetails, setShowDetails] = useState(false);
    const [copiedField, setCopiedField] = useState('');
    const [cardActive,  setCardActive]  = useState(true);
    const [onlineEnabled, setOnlineEnabled] = useState(true);
    const [intlEnabled,   setIntlEnabled]   = useState(false);
    const [freezing, setFreezing] = useState(false);
    const [frozen,   setFrozen]   = useState(false);

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const copy = (text: string, field: string) => {
        navigator.clipboard.writeText(text.replace(/\s/g, ''));
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };

    const handleFreeze = () => {
        setFreezing(true);
        setTimeout(() => { setFreezing(false); setFrozen(f => !f); }, 1200);
    };

    const maskedNumber = showDetails ? CARD.number : '•••• •••• •••• ' + CARD.number.slice(-4);
    const maskedCVV    = showDetails ? CARD.cvv : '•••';
    const maskedExpiry = showDetails ? CARD.expiry : '••/••';
    const spendPct     = (CARD.spent / CARD.limit) * 100;

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#5856D6', .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate('/wallet')}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Virtual Card
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(frozen ? B.red : B.green, .15), border: `1px solid ${alpha(frozen ? B.red : B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: frozen ? B.red : B.green, boxShadow: `0 0 6px ${frozen ? B.red : B.green}` }} />
                        <Typography sx={{ color: frozen ? B.red : B.green, fontSize: '.68rem', fontWeight: 700 }}>
                            {frozen ? 'FROZEN' : 'ACTIVE'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ══ CARD VISUAL ══ */}
                <Box sx={{
                    borderRadius: '24px', p: 3, mb: 2, position: 'relative', overflow: 'hidden', minHeight: 200,
                    background: frozen
                        ? 'linear-gradient(145deg, #1a1a2e 0%, #2d2d44 100%)'
                        : 'linear-gradient(145deg, #0F0C29 0%, #302B63 60%, #24243e 100%)',
                    boxShadow: frozen
                        ? `0 20px 50px ${alpha('#000', .4)}`
                        : `0 20px 50px ${alpha('#302B63', .5)}`,
                    transition: 'all .4s',
                    filter: frozen ? 'grayscale(0.6) brightness(0.7)' : 'none',
                }}>
                    {/* card glow orbs */}
                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#8B6FFF', .25)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .15)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                    {/* chip + network */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box sx={{ width: 36, height: 28, borderRadius: '6px', background: 'linear-gradient(135deg, #D4A843, #F5C842)', opacity: frozen ? .4 : 1 }} />
                        <Typography sx={{ color: alpha('#fff', frozen ? .3 : .7), fontWeight: 800, fontSize: '1.1rem', fontStyle: 'italic', letterSpacing: '.04em' }}>VISA</Typography>
                    </Box>

                    {/* card number */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
                        <Typography sx={{ color: alpha('#fff', frozen ? .35 : .9), fontFamily: "'DM Mono', monospace, 'DM Sans'", fontSize: { xs: '.9rem', sm: '1rem' }, letterSpacing: '.14em', fontWeight: 500 }}>
                            {maskedNumber}
                        </Typography>
                        <IconButton size="small" onClick={() => copy(CARD.number, 'number')} sx={{ color: alpha('#fff', .4), p: .5, '&:hover': { color: '#fff' } }}>
                            {copiedField === 'number' ? <CheckOutlined sx={{ fontSize: 15, color: B.green }} /> : <ContentCopyOutlined sx={{ fontSize: 15 }} />}
                        </IconButton>
                    </Box>

                    {/* expiry + cvv + holder */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.55rem', letterSpacing: '.12em', textTransform: 'uppercase', mb: .25 }}>Card Holder</Typography>
                            <Typography sx={{ color: alpha('#fff', frozen ? .4 : .85), fontWeight: 600, fontSize: '.8rem', letterSpacing: '.04em' }}>{CARD.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.55rem', letterSpacing: '.12em', textTransform: 'uppercase', mb: .25 }}>Expires</Typography>
                                <Typography sx={{ color: alpha('#fff', frozen ? .4 : .85), fontWeight: 600, fontSize: '.8rem', letterSpacing: '.06em' }}>{maskedExpiry}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.55rem', letterSpacing: '.12em', textTransform: 'uppercase', mb: .25 }}>CVV</Typography>
                                <Box display="flex" alignItems="center" gap={.5}>
                                    <Typography sx={{ color: alpha('#fff', frozen ? .4 : .85), fontWeight: 600, fontSize: '.8rem', letterSpacing: '.06em' }}>{maskedCVV}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* frozen overlay */}
                    {frozen && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: alpha('#000', .3), borderRadius: '24px' }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <BlockOutlined sx={{ color: alpha('#fff', .5), fontSize: 36, mb: .5 }} />
                                <Typography sx={{ color: alpha('#fff', .6), fontWeight: 700, fontSize: '.85rem', letterSpacing: '.08em' }}>CARD FROZEN</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* ── Card actions ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                    {[
                        { label: showDetails ? 'Hide Details' : 'Show Details', icon: showDetails ? VisibilityOffOutlined : VisibilityOutlined, action: () => setShowDetails(s => !s), color: B.purple },
                        { label: freezing ? 'Working…' : frozen ? 'Unfreeze' : 'Freeze Card', icon: frozen ? CheckCircleOutline : BlockOutlined, action: handleFreeze, color: frozen ? B.green : B.red },
                        { label: 'New Card', icon: RefreshOutlined, action: () => {}, color: B.blue },
                    ].map(btn => {
                        const Icon = btn.icon;
                        return (
                            <Box key={btn.label} onClick={btn.action} sx={{
                                ...glassCard, p: 1.75, textAlign: 'center', cursor: 'pointer',
                                transition: 'all .18s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(btn.color, .2)}` },
                            }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: alpha(btn.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: .75 }}>
                                    <Icon sx={{ color: btn.color, fontSize: 18 }} />
                                </Box>
                                <Typography sx={{ color: textMid, fontSize: '.65rem', fontWeight: 600, lineHeight: 1.2 }}>{btn.label}</Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Spend overview ── */}
                <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem' }}>Monthly Spend</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>Limit: {fmt(CARD.limit)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.4rem', fontWeight: 700 }}>{fmt(CARD.spent)}</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.78rem', alignSelf: 'flex-end', mb: .5 }}>{spendPct.toFixed(0)}% used</Typography>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 4, background: alpha(B.purple, .1), overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${spendPct}%`, borderRadius: 4, background: spendPct > 80 ? `linear-gradient(90deg, ${B.orange}, ${B.red})` : `linear-gradient(90deg, ${B.purple}, ${B.light})`, transition: 'width .6s cubic-bezier(.16,1,.3,1)' }} />
                    </Box>
                    <Typography sx={{ color: textFaint, fontSize: '.7rem', mt: 1 }}>
                        {fmt(CARD.limit - CARD.spent)} remaining this month
                    </Typography>
                </Box>

                {/* ── Card controls ── */}
                <Box sx={{ ...glassCard, overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.9rem', color: textPri }}>Card Controls</Typography>
                    </Box>
                    {[
                        { label: 'Online Payments',       sub: 'E-commerce & subscriptions', value: onlineEnabled, onChange: () => setOnlineEnabled(v => !v) },
                        { label: 'International Use',     sub: 'Transactions outside Nigeria', value: intlEnabled,  onChange: () => setIntlEnabled(v => !v)  },
                    ].map((ctrl, i) => (
                        <React.Fragment key={ctrl.label}>
                            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.84rem' }}>{ctrl.label}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{ctrl.sub}</Typography>
                                </Box>
                                <Switch checked={ctrl.value} onChange={ctrl.onChange} size="small"
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: B.purple }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: B.purple } }} />
                            </Box>
                            {i === 0 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                        </React.Fragment>
                    ))}
                </Box>

                {/* ── Recent card transactions ── */}
                <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>Card Transactions</Typography>
                        <Button size="small" onClick={() => navigate('/history')}
                                sx={{ color: B.purple, fontWeight: 600, fontSize: '.7rem', textTransform: 'none', px: 1, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            See all
                        </Button>
                    </Box>
                    {RECENT_TXS.map((tx, i) => {
                        const Icon = tx.icon;
                        return (
                            <React.Fragment key={tx.id}>
                                <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: alpha(tx.color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Icon sx={{ color: tx.color, fontSize: 18 }} />
                                    </Box>
                                    <Box flex={1} minWidth={0}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '.84rem', color: textPri, letterSpacing: '-.015em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.title}</Typography>
                                        <Typography sx={{ fontSize: '.68rem', color: textFaint, mt: .15 }}>{tx.sub} · {tx.date}</Typography>
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '.86rem', color: textPri, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                        −₦{tx.amount.toLocaleString()}
                                    </Typography>
                                </Box>
                                {i < RECENT_TXS.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                            </React.Fragment>
                        );
                    })}
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default CardPage;